import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Container from '@material-ui/core/Container';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import CardContent from '@material-ui/core/CardContent';
import Link from '@material-ui/core/Link';
import InputAdornment from '@material-ui/core/InputAdornment';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import Head from '../../../components/Head';
import { useAuth } from '../../../contexts/auth';
import AuthHeader from '../../../components/Auth/Header';
import Card from '../../../components/Auth/Card';
import ErrorMessage from '../../../components/ErrorMessage';
import AuthFooter from '../../../components/Auth/Footer';
import { AtSignIcon, LockIcon } from '../../../assets/icons';
import useStyles from './styles';
import useAuthStyles from '../../../styles/pages/auth';

interface Values {
  email: string;
  password: string;
}

enum ErrorTranslate {
  'wrong-password' = 'Senha inválida!',
  'user-not-found' = 'Email não localizado!',
  'user-not-eligible' = 'Seu cadastro não está habilitado para esta ação. Em caso de dúvidas, fale com frotaexecutiva@pepsico.com',
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email é obrigatório')
    .email('Digite um email válido'),
  password: Yup.string().required('Senha é obrigatória'),
});

function FormLogin() {
  const classes = useStyles();
  const authClasses = useAuthStyles();
  const auth = useAuth();

  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFormik = async (
    values: Values,
    formikHelpers: FormikHelpers<Values>
  ) => {
    try {
      handleClose();
      await auth.login(values.email, values.password);
      formikHelpers.resetForm({ values: {} as Values });
      setHasSuccess(true);
    } catch (error: any) {
      setHasError(true);
      setErrorMessage(
        ErrorTranslate[
          error.response.data.message as keyof typeof ErrorTranslate
        ]
      );
    }
    localStorage.removeItem('@pepsico-order');
    localStorage.removeItem('@pepsico-carSelected');
    localStorage.removeItem('@pepsico-cars');
    formikHelpers.setSubmitting(false);
  };

  const handleClose = () => {
    setHasError(false);
    setHasSuccess(false);
  };

  return (
    <Container maxWidth="sm">
      <Head>Login</Head>
      <Formik
        initialValues={{} as Values}
        validationSchema={validationSchema}
        onSubmit={handleFormik}
      >
        {({ isSubmitting, values, handleChange, touched, errors }) => (
          <Form className={authClasses.container}>
            <Card>
              <AuthHeader />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} className={authClasses.inputsContainer}>
                    <TextField
                      id="email"
                      name="email"
                      label="Email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      InputProps={{
                        className: authClasses.inputs,
                        endAdornment: (
                          <InputAdornment position="end">
                            <AtSignIcon />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      id="password"
                      name="password"
                      label="Senha"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        className: authClasses.inputs,
                        endAdornment: (
                          <InputAdornment position="end">
                            <LockIcon />
                          </InputAdornment>
                        ),
                      }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.forgotContainer}>
                    <Link component={RouterLink} to="/esqueci-a-senha">
                      <Typography color="textSecondary">
                        Esqueci a senha.
                      </Typography>
                    </Link>
                  </Grid>
                  <Grid item xs={12}>
                    <ErrorMessage
                      open={hasError || hasSuccess}
                      handleClose={handleClose}
                      title={hasError ? 'Erro' : 'Sucesso'}
                      severity={hasError ? 'error' : 'success'}
                      message={
                        hasError ? (
                          <>{errorMessage}</>
                        ) : hasSuccess ? (
                          <>
                            Email de recuperação de senha enviado. Favor chegar
                            sua caixa de emails e clicar no link.
                          </>
                        ) : null
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      disabled={isSubmitting}
                      className={authClasses.submitButton}
                    >
                      {isSubmitting ? 'Aguarde...' : 'Entrar'}
                    </Button>

                    <div style={{marginTop: '8px', fontWeight: 'bold'}}>
                      Dúvidas?{' '}
                      <Typography
                        component={'a'}
                        href="mailto:frotaexecutiva@pepsico.com"
                      >
                        frotaexecutiva@pepsico.com
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {/*<Box className={classes.registerBox}>*/}
            {/*  <Typography color="inherit">*/}
            {/*    Ainda não possui um cadastro?*/}
            {/*  </Typography>*/}
            {/*  <Link component={RouterLink} to="/registro" color="inherit">*/}
            {/*    Registre-se agora!*/}
            {/*  </Link>*/}
            {/*</Box>*/}
            <Box px={2.5} display="none">
              <Button
                component={RouterLink}
                to="/h1n1"
                fullWidth
                disableElevation
                variant="contained"
                size="large"
                className={classes.h1n1Button}
              >
                Campanha de Vacinação Contra Gripe
              </Button>
            </Box>
            <Box mt={6}>
              <AuthFooter />
            </Box>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

export default FormLogin;
